import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { forkJoin, Subscription } from 'rxjs';

import { TYPE_TOAST } from 'src/app/system/core/enums/ToastsEnums';
import { Employee } from 'src/app/system/core/models/employee.interface';
import { User } from 'src/app/system/core/models/user.interface';
import { AuthService } from 'src/app/system/services/auth.service';
import { FcmService } from 'src/app/system/services/fcm.service';
import { MessagesService } from 'src/app/system/services/message.service';
import { NotificationItem, NotificationStateService } from 'src/app/system/services/notification-state.service';
import { NotificationsService } from 'src/app/system/services/notifications.service';
import { UserService } from 'src/app/system/services/user.service';
import { LayoutService } from '../../services/layout.service';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {
  @ViewChild('searchinput') searchInput!: ElementRef;
  @ViewChild('menubutton') menuButton!: ElementRef;
  @ViewChild('notificationDropdown') notificationDropdown!: ElementRef;
  @ViewChild('notificationButton') notificationButton!: ElementRef;

  public menu: MenuItem[] = [];
  public searchActive: boolean = false;
  public employee!: Employee;
  public user!: User;
  public isLoading: boolean = false;

  public notifications: NotificationItem[] = [];
  public hasUnreadNotifications: boolean = false;
  public showNotificationsDropdown: boolean = false;

  private notificationSubscription!: Subscription;
  private unreadSubscription!: Subscription;

  public menuItems = [
    {
      label: 'Perfil',
      items: [
        { label: 'Cerra sesión', icon: 'pi pi-power-off', command: this.logout.bind(this) },
      ],
    },
  ];

  constructor(
    public layoutService: LayoutService,
    public AuthService: AuthService,
    public UserService: UserService,
    private MessagesService: MessagesService,
    private notificationService: NotificationsService,
    private notificationStateService: NotificationStateService,
    public router: Router,
    private fcmService: FcmService,
  ) { }

  ngOnInit(): void {
    this.fcmService.installFCMServiceWorker();
    this.fcmService.listenForMessages();

    this.employee = this.UserService.Employee;
    this.user = this.UserService.User;
    this.loadNotifications();
    this.subscribeToNotificationState();
  }

  ngOnDestroy(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.unreadSubscription) {
      this.unreadSubscription.unsubscribe();
    }
  }

  private subscribeToNotificationState(): void {
    this.notificationSubscription = this.notificationStateService.notifications$.subscribe(
      (notifications: NotificationItem[]) => {
        this.notifications = notifications;
      }
    );

    this.unreadSubscription = this.notificationStateService.hasUnreadNotifications$.subscribe(
      (hasUnread: boolean) => {
        this.hasUnreadNotifications = hasUnread;
      }
    );
  }

  loadNotifications() {
    this.notificationService.getNotifications({ page: 1, page_size: 10 }).subscribe({
      next: (response: any) => {
        this.notificationStateService.setNotifications(response.items);
      },
      error: (error: any) => {
        this.MessagesService.ShowMessage(TYPE_TOAST.ERROR, "Error", "Ocurrió un error al cargar las notificaciones");
      }
    });
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  removeTab(event: MouseEvent, item: MenuItem, index: number) {
    this.layoutService.onTabClose(item, index);
    event.preventDefault();
  }

  get layoutTheme(): string {
    return this.layoutService.config.layoutTheme;
  }

  get colorScheme(): string {
    return this.layoutService.config.colorScheme;
  }

  get tabs(): MenuItem[] {
    return this.layoutService.tabs;
  }

  getPicture(): string {
    return this.UserService.profilePhoto;
  }

  profile() {
    this.employee.name = "as"
  }

  logout(): void {
    this.AuthService.logout();
  }

  toggleNotificationsDropdown() {
    this.showNotificationsDropdown = !this.showNotificationsDropdown;

    if (this.showNotificationsDropdown && this.notifications.length === 0) {
      this.loadNotifications();
    }
  }

  openNotification(notification: NotificationItem) {
    if (notification.id) {
      this.markAsRead(notification.id);
    }

    this.router.navigate(['/notifications']);
    this.showNotificationsDropdown = false;
  }

  markAsRead(notificationId?: string) {
    if (notificationId) {
      this.notificationService.markAsRead(notificationId).subscribe({
        next: () => {
          this.notificationStateService.markAsRead(notificationId);
        },
        error: (error) => {
          console.error('Error marking notification as read:', error);
        }
      });
    } else {
      const notificationsWithId = this.notificationStateService.getNotificationsWithId();
      const unreadNotificationsWithId = notificationsWithId.filter(n => !n.read && n.id);

      if (unreadNotificationsWithId.length === 0) {
        this.MessagesService.ShowMessage(TYPE_TOAST.INFO, "Información", "No hay notificaciones sin leer");
        return;
      }

      const markAsReadRequests = unreadNotificationsWithId.map(notification =>
        this.notificationService.markAsRead(notification.id!)
      );

      forkJoin(markAsReadRequests).subscribe({
        next: () => {
          this.notificationStateService.markAllAsRead();
          this.MessagesService.ShowMessage(TYPE_TOAST.SUCCESS, "Éxito", "Todas las notificaciones han sido marcadas como leídas");
        },
        error: (error) => {
          console.error('Error marking all notifications as read:', error);
          this.MessagesService.ShowMessage(TYPE_TOAST.ERROR, "Error", "Ocurrió un error al marcar las notificaciones como leídas");
        }
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.showNotificationsDropdown) {
      const clickedInsideDropdown = this.notificationDropdown?.nativeElement.contains(event.target);
      const clickedOnButton = this.notificationButton?.nativeElement.contains(event.target);

      if (!clickedInsideDropdown && !clickedOnButton) {
        this.showNotificationsDropdown = false;
      }
    }
  }
}
